import { useNavigate } from "react-router-dom";
import { Phone, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@util/tailwind";
import type { CallSession } from "@type/agent";
import {
    formatDuration,
    statusBadgeConfig,
    getScoreColor,
    sentimentChipConfig,
    chipVariantStyles,
    chipDotColor,
} from "@util/agent-scoring";
import { formatMYT } from "@util/date";

export type SortField =
    | "timestamp"
    | "contactName"
    | "duration"
    | "sentiment"
    | "score"
    | "status";

interface CallSessionTableProps {
    sessions: CallSession[];
    loading: boolean;
    sortField: SortField;
    sortDirection: "asc" | "desc";
    onSort: (field: SortField) => void;
    campaignRetryAttempts?: number;
}


// Track widths per column: Contact | Phone # | Call ID | Type | Campaign | Date |
// Messages | Duration | Attempt | Score | Sentiment | Status.
// Messages/Duration were 60/80 — too narrow for their 8-char uppercase headers, so
// the labels bled into each other. Widened to 84/90 and rebalanced from the
// over-wide Phone #/Call ID/Campaign tracks so the total min-width stays 1074px
// (matches min-w-[1080px] below — no horizontal scroll introduced).
const GRID_COLS = "minmax(120px, 0.85fr) 100px 78px 80px minmax(90px, 0.9fr) 100px 84px 90px 72px 68px 82px 110px";


export function CallSessionTable({
    sessions,
    loading,
    sortField,
    sortDirection,
    onSort,
    campaignRetryAttempts,
}: CallSessionTableProps) {
    const navigate = useNavigate();

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (sessions.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Phone className="w-8 h-8 mb-2" />
                <p className="text-sm font-medium text-gray-500">No sessions found.</p>
                <p className="text-xs mt-1 text-gray-400">Try adjusting your filters or search to see results.</p>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden overflow-x-auto">
            {/* Header */}
            <div
                className="grid bg-[#fafbfd] dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 min-w-[1080px] px-5 py-[9px]"
                style={{ gridTemplateColumns: GRID_COLS }}
            >
                <div className="px-2">
                    <button onClick={() => onSort("contactName")} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors">
                        Contact
                        {sortField === "contactName" ? (sortDirection === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />) : <ChevronDown className="w-3.5 h-3.5 opacity-30" />}
                    </button>
                </div>
                <div className="px-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Phone #</span>
                </div>
                <div className="px-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Call ID</span>
                </div>
                <div className="px-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</span>
                </div>
                <div className="px-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Campaign</span>
                </div>
                <div className="px-2">
                    <button onClick={() => onSort("timestamp")} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors">
                        Date
                        {sortField === "timestamp" ? (sortDirection === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />) : <ChevronDown className="w-3.5 h-3.5 opacity-30" />}
                    </button>
                </div>
                <div className="px-2 text-center">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Messages</span>
                </div>
                <div className="px-2 text-center">
                    <button onClick={() => onSort("duration")} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap hover:text-gray-600 transition-colors">
                        Duration
                        {sortField === "duration" ? (sortDirection === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />) : <ChevronDown className="w-3.5 h-3.5 opacity-30" />}
                    </button>
                </div>
                <div className="px-2 text-center">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Attempt</span>
                </div>
                <div className="px-2 text-center">
                    <button onClick={() => onSort("score")} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors">
                        Score
                        {sortField === "score" ? (sortDirection === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />) : <ChevronDown className="w-3.5 h-3.5 opacity-30" />}
                    </button>
                </div>
                <div className="px-2 text-center">
                    <button onClick={() => onSort("sentiment")} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors">
                        Sentiment
                        {sortField === "sentiment" ? (sortDirection === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />) : <ChevronDown className="w-3.5 h-3.5 opacity-30" />}
                    </button>
                </div>
                <div className="px-2">
                    <button onClick={() => onSort("status")} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors">
                        Status
                        {sortField === "status" ? (sortDirection === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />) : <ChevronDown className="w-3.5 h-3.5 opacity-30" />}
                    </button>
                </div>
            </div>

            {/* Rows */}
            {sessions.map((session) => {
                const statusConfig = statusBadgeConfig[session.status] || {
                    label: session.status,
                    variant: "default" as const,
                };
                const isUnknown = !session.contactName || session.contactName === "Unknown";
                const attempt = (session.retryCount ?? 0) + 1;

                return (
                    <div
                        key={session.id}
                        onClick={() => navigate(`/agent/calls/${session.id}`)}
                        className="grid items-center border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-[#f8f9fd] dark:hover:bg-gray-800/70 cursor-pointer transition-[background] duration-100 min-w-[1080px] px-5 py-[11px]"
                        style={{ gridTemplateColumns: GRID_COLS }}
                    >
                        {/* Contact: avatar + name + test badge */}
                        <div className="px-2 flex items-center gap-2 min-w-0">
                            <div
                                className={cn(
                                    "w-[30px] h-[30px] rounded-[7px] flex-shrink-0 flex items-center justify-center text-xs font-bold",
                                    isUnknown
                                        ? "bg-gray-100 dark:bg-gray-800 text-gray-400"
                                        : "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                                )}
                            >
                                {isUnknown ? "?" : session.contactName![0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                                        {session.contactName || "Unknown"}
                                    </span>
                                    {session.isTestCall && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-50 text-indigo-600 border border-indigo-200 flex-shrink-0">
                                            Test
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10.5px] font-mono text-gray-400 mt-px">
                                    <span className="text-gray-300 dark:text-gray-600 mr-0.5">ID:</span>{session.id.slice(0, 8)}
                                </div>
                            </div>
                        </div>

                        {/* Phone # */}
                        <div className="px-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">
                                {session.contactPhone || "--"}
                            </span>
                        </div>

                        {/* Call ID */}
                        <div className="px-2">
                            <span className="text-[11px] font-mono text-gray-400">
                                {session.id.slice(0, 8)}
                            </span>
                        </div>

                        {/* Type */}
                        <div className="px-2">
                            {session.isTestCall ? (
                                <span className="inline-flex items-center px-[7px] py-0.5 text-[11px] font-semibold rounded bg-indigo-50 text-indigo-600">
                                    Test
                                </span>
                            ) : (
                                <span className={cn(
                                    "inline-flex items-center px-[7px] py-0.5 text-[11px] font-semibold rounded",
                                    attempt > 1
                                        ? "bg-amber-50 text-amber-700"
                                        : "bg-gray-100 text-gray-500"
                                )}>
                                    {attempt > 1
                                        ? `Retry ${session.retryCount}/${session.maxRetryAttempts ?? campaignRetryAttempts ?? "?"}`
                                        : "First try"}
                                </span>
                            )}
                        </div>

                        {/* Campaign */}
                        <div className="px-2 min-w-0">
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">
                                {session.campaignName || "--"}
                            </span>
                        </div>

                        {/* Date: 2-line */}
                        <div className="px-2">
                            <div className="text-xs text-gray-500 dark:text-gray-300">
                                {formatMYT(session.timestamp, "D MMM YYYY")}
                            </div>
                            <div className="text-[10.5px] text-gray-400 mt-px">
                                {formatMYT(session.timestamp, "h:mm A")}
                            </div>
                        </div>

                        {/* Messages */}
                        <div className={cn(
                            "px-2 text-center text-xs font-mono",
                            !session.messageCount ? "text-gray-400" : "text-gray-700 dark:text-gray-300"
                        )}>
                            {session.messageCount || "\u2014"}
                        </div>

                        {/* Duration */}
                        <div className={cn(
                            "px-2 text-center text-xs",
                            session.duration > 0 ? "text-gray-700 dark:text-gray-300" : "text-gray-400"
                        )}>
                            {session.duration > 0 ? formatDuration(session.duration) : "0s"}
                        </div>

                        {/* Attempt */}
                        <div className="px-2 text-center text-xs font-mono text-gray-700 dark:text-gray-300">
                            {session.isTestCall
                                ? <span className="text-gray-400 not-italic text-[10px]">Test</span>
                                : `${attempt}/${(session.maxRetryAttempts ?? campaignRetryAttempts ?? 0) + 1}`
                            }
                        </div>

                        {/* Score */}
                        <div className="px-2 text-center">
                            {session.score > 0 ? (
                                <span className={cn("font-mono text-[12.5px] font-bold", getScoreColor(session.score))}>
                                    {session.score}%
                                </span>
                            ) : (
                                <span className="text-xs text-gray-400">{"\u2014"}</span>
                            )}
                        </div>

                        {/* Sentiment */}
                        <div className="px-2 text-center">
                            {(() => {
                                const sc = sentimentChipConfig[session.sentiment];
                                return (
                                    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border", chipVariantStyles[sc.variant])}>
                                        <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[sc.variant])} />
                                        {sc.label}
                                    </span>
                                );
                            })()}
                        </div>

                        {/* Status: badge with dot + fail reason */}
                        <div className="px-2">
                            <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap", chipVariantStyles[statusConfig.variant])}>
                                <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[statusConfig.variant])} />
                                {statusConfig.label}
                            </span>
                            {session.status === "failed" && session.failReason && (
                                <div
                                    className="text-[10px] text-red-400 mt-0.5 truncate max-w-[120px] cursor-help"
                                    title={`Fail reason: ${session.failReason}`}
                                >
                                    {session.failReason}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
